"use client";

/**
 * Overlay de transición de auth (login/logout).
 *
 * Por qué existe: cuando el usuario hace login o logout, hay un lapso (cientos
 * de ms) en que la pantalla anterior ya se fue pero la nueva todavía no pintó
 * — eso deja el `<body>` mostrando sólo su `background`, lo que se percibe como
 * un "flash blanco" feo.
 *
 *  - **Login** hace `window.location.assign(...)` (hard reload). Durante el
 *    reload el navegador descarta la pantalla actual y muestra el fondo del
 *    body hasta que llega el HTML nuevo y React monta. Para tapar ese hueco
 *    usamos un script inline que, si encuentra el flag en sessionStorage,
 *    pinta el overlay ANTES de que React monte.
 *  - **Logout** usa `router.replace("/login")` (soft nav RSC). El overlay React
 *    cubre la transición y se desvanece con fade.
 *
 * El flag (`vortigon.auth-transition`) en sessionStorage es el puente entre el
 * tab antes y después del hard reload. Se limpia automáticamente cuando el
 * overlay desaparece.
 */

import { useEffect, useSyncExternalStore } from "react";

const FLAG_KEY = "vortigon.auth-transition";
const BOOT_ID = "__vortigon_auth_overlay_boot";
const FADE_MS = 260;

// ---------------- store ----------------
let _visible = false;
const _listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function getSnapshot() {
  return _visible;
}

function getServerSnapshot() {
  return false;
}

function setVisible(next: boolean) {
  if (_visible === next) return;
  _visible = next;
  _listeners.forEach((cb) => cb());
}

// ---------------- API pública ----------------

/** Activa el overlay y persiste el flag para sobrevivir a un hard reload. */
export function showAuthTransition() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FLAG_KEY, "1");
  } catch {
    // ignore (private mode / quota)
  }
  setVisible(true);
}

/** Apaga el overlay y limpia el flag. */
export function hideAuthTransition() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(FLAG_KEY);
  } catch {
    // ignore
  }
  setVisible(false);
}

/**
 * ¿Hay una transición de auth (login/logout) tapando la pantalla ahora mismo?
 * Lo usa el AppShell para NO montar el SessionExpiredScreen mientras un logout
 * está en curso: durante el logout `status` cae a "unauthenticated" por un
 * instante y, sin este chequeo, el cartel de expiración asomaría debajo del
 * overlay (el flicker "Tu sesión expiró"). Mira el store en memoria y, como
 * respaldo, el flag en sessionStorage (que sobrevive al hard reload).
 */
export function isAuthTransitionActive(): boolean {
  if (_visible) return true;
  if (typeof window === "undefined") return false;
  try {
    return !!window.sessionStorage.getItem(FLAG_KEY);
  } catch {
    return false;
  }
}

// ---------------- boot script (inline en root layout) ----------------

/**
 * Script JS sincrónico que se ejecuta antes que React monte. Si el flag está
 * presente (caso: el login disparó un hard reload), inyecta un overlay DOM
 * con el logo para tapar el flash blanco. El componente React lo remueve en
 * cuanto toma el control y dibuja su propio overlay encima.
 *
 * Mantener self-contained: nada de imports, nada de CSS externo, sólo strings.
 */
export const authOverlayBootScript = `(function(){try{
if(!window.sessionStorage||!sessionStorage.getItem(${JSON.stringify(FLAG_KEY)}))return;
if(document.getElementById(${JSON.stringify(BOOT_ID)}))return;
var dark=document.documentElement.classList.contains('dark');
var bg=dark?'#0b0b0c':'#ffffff';
var fg=dark?'#e7e7ea':'#283040';
var ac=dark?'#8fb3c7':'#3e6e82';
var d=document.createElement('div');
d.id=${JSON.stringify(BOOT_ID)};
d.setAttribute('aria-hidden','true');
d.style.cssText='position:fixed;inset:0;z-index:2147483646;background:'+bg+';display:flex;align-items:center;justify-content:center;pointer-events:none';
d.innerHTML='<style>@keyframes __vtg_pulse{0%,100%{opacity:.65;transform:scale(.96)}50%{opacity:1;transform:scale(1)}}</style><svg width="56" height="56" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style="animation:__vtg_pulse 1.4s ease-in-out infinite"><path d="M16 3 L27 7 V15.5 C27 22.5 22.5 27.5 16 30 C9.5 27.5 5 22.5 5 15.5 V7 Z" stroke="'+fg+'" stroke-width="1.4" stroke-linejoin="round"/><path d="M9.5 12 L16 22 L22.5 12" stroke="'+fg+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.5 12 L16 17 L19.5 12" stroke="'+ac+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
(document.body||document.documentElement).appendChild(d);
}catch(e){}})();`;

// ---------------- componente React ----------------

export function AuthTransitionOverlay() {
  const visible = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Sincronización con el flag de sessionStorage en el primer mount: si venimos
  // de un hard reload (login), el flag está y arrancamos visible; después de un
  // par de frames + un pequeño settle, fade out.
  useEffect(() => {
    let hasFlag = false;
    try {
      hasFlag = !!window.sessionStorage.getItem(FLAG_KEY);
    } catch {
      hasFlag = false;
    }

    // Eliminar el div pintado por el boot script — React ya tiene su propio
    // overlay arriba con el mismo z-index y estética, así que la transición es
    // visualmente continua.
    const boot = document.getElementById(BOOT_ID);
    if (boot) boot.remove();

    if (!hasFlag) return;

    // Mostrar inmediato + agendar fade-out cuando el frame ya pintó.
    setVisible(true);
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        // Damos un settle de ~120ms para que el contenido detrás termine de
        // hidratarse antes de revelarlo. Si la página tarda más, el overlay
        // igualmente se desvanece — no bloqueamos al usuario.
        timeout = setTimeout(() => {
          hideAuthTransition();
        }, 120);
      });
      // raf2 se cancela vía cleanup de raf1 indirectamente; rAF anidados son
      // baratos y no leakean.
      void raf2;
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-state={visible ? "open" : "closed"}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        background: "var(--color-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <svg
        width={56}
        height={56}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ animation: "__vtg_pulse 1.4s ease-in-out infinite" }}
      >
        <path
          d="M16 3 L27 7 V15.5 C27 22.5 22.5 27.5 16 30 C9.5 27.5 5 22.5 5 15.5 V7 Z"
          stroke="var(--color-fg)"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path
          d="M9.5 12 L16 22 L22.5 12"
          stroke="var(--color-fg)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.5 12 L16 17 L19.5 12"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <style>{`@keyframes __vtg_pulse{0%,100%{opacity:.65;transform:scale(.96)}50%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
